import { useState } from 'react';

import sliders from './affiliates.module.css';
import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';

import { type CalculatorPlan, COMMISSION, formatUsd, REWARDFUL } from './kit';

export default function AffiliatesCalculator({
  plans,
}: {
  plans: CalculatorPlan[];
}) {
  const [counts, setCounts] = useState(() => plans.map((plan) => plan.start));

  const monthly = plans.reduce(
    (sum, plan, index) => sum + plan.monthly * counts[index] * COMMISSION,
    0,
  );
  const referrals = counts.reduce((sum, count) => sum + count, 0);

  return (
    <section id='calculator' className='scroll-mt-8 py-6 sm:py-8'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 0%'
          halftoneReach={40}
          className='px-6 py-14 sm:px-12 sm:py-20 lg:px-16'
        >
          <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
            <div className='lg:col-span-5'>
              <SectionHeading
                size='md'
                title='What could you earn?'
                tail='Move the sliders.'
                lead='Set how many paying customers you might refer on each plan. The estimate uses today’s monthly prices and your 30% share.'
              />
              <p className={clsxm('mt-6 text-sm leading-6', tone.quiet)}>
                Yearly plans pay the same 30% on the yearly payment. This is an
                estimate, not a promise of earnings.
              </p>
            </div>

            <div
              className={clsxm(
                'rounded-[26px] p-6 shadow-[0_30px_60px_-36px_rgba(30,27,75,0.5)] sm:p-8 lg:col-span-7',
                tone.surface,
                tone.line,
              )}
            >
              <div className='space-y-7'>
                {plans.map((plan, index) => {
                  const id = `referrals-${index}`;
                  const count = counts[index];
                  const percent = (count / plan.max) * 100;
                  return (
                    <div key={plan.name}>
                      <div className='flex items-baseline justify-between gap-4'>
                        <label
                          htmlFor={id}
                          className={clsxm(
                            'text-[15px] font-semibold',
                            tone.ink,
                          )}
                        >
                          {plan.name}{' '}
                          <span className={clsxm('font-normal', tone.quiet)}>
                            {plan.from ? 'from ' : ''}
                            {formatUsd(plan.monthly)}/mo
                          </span>
                        </label>
                        <span
                          className={clsxm('text-sm tabular-nums', tone.body)}
                        >
                          <span className={clsxm('font-semibold', tone.ink)}>
                            {count}
                          </span>{' '}
                          {count === 1 ? 'customer' : 'customers'}
                        </span>
                      </div>
                      <input
                        id={id}
                        type='range'
                        min={0}
                        max={plan.max}
                        step={1}
                        value={count}
                        onChange={(event) =>
                          setCounts((current) =>
                            current.map((value, i) =>
                              i === index ? Number(event.target.value) : value,
                            ),
                          )
                        }
                        className={sliders.range}
                        style={{
                          background: `linear-gradient(to right, rgb(var(--tw-color-primary-600)) ${percent}%, rgb(var(--tw-color-primary-100)) ${percent}%)`,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div className='mt-8 rounded-[20px] bg-[#0d0c1f] px-6 py-5 text-white'>
                <p className='text-[13px] font-medium text-[#b7b7d2]'>
                  Your estimated commission from {referrals}{' '}
                  {referrals === 1 ? 'customer' : 'customers'}
                </p>
                <div className='mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1'>
                  <p
                    dir='ltr'
                    className={clsxm(
                      styles.display,
                      'text-4xl font-semibold tabular-nums sm:text-5xl',
                    )}
                    aria-live='polite'
                  >
                    {formatUsd(Math.round(monthly))}
                    <span className='ms-1.5 text-lg font-medium tracking-normal text-[#b7b7d2]'>
                      / month
                    </span>
                  </p>
                  <p
                    dir='ltr'
                    className='text-lg font-medium tabular-nums text-[#d9d9ee]'
                  >
                    {formatUsd(Math.round(monthly * 12))} / year
                  </p>
                </div>
              </div>

              <a
                href={REWARDFUL.signup}
                className='mt-6 inline-flex items-center gap-1.5 text-[15px] font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400'
              >
                Start earning
                <ArrowGlyph className='h-3.5 w-3.5' />
              </a>
            </div>
          </div>
        </Stage>
      </div>
    </section>
  );
}
